/**
 * Service Catalog API client hooks using TanStack Query
 * Provides hooks for browsing the service catalog (read-only operations)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  ServiceCatalogResponse,
  ServiceCatalogParams,
  ServiceDetail
} from '@/lib/types'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

/**
 * Query key factory for service catalog
 */
export const serviceCatalogKeys = {
  all: ['service-catalog'] as const,
  list: (params: ServiceCatalogParams = {}) =>
    ['service-catalog', 'list', params] as const,
  detail: (serviceName: string) =>
    ['service-catalog', 'detail', serviceName] as const
}

/**
 * Fetch services from the service catalog API
 */
export async function fetchServiceCatalog(
  params: ServiceCatalogParams = {}
): Promise<ServiceCatalogResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/service-catalog', baseUrl)

  if (params.search) {
    url.searchParams.set('search', params.search)
  }
  if (params.areaPath) {
    url.searchParams.set('areaPath', params.areaPath)
  }
  if (params.ownerTeam) {
    url.searchParams.set('ownerTeam', params.ownerTeam)
  }
  if (params.country) {
    url.searchParams.set('country', params.country)
  }
  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit))
  }
  if (params.offset !== undefined) {
    url.searchParams.set('offset', String(params.offset))
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch a single service by name from the service catalog API
 */
export async function fetchServiceDetail(
  serviceName: string
): Promise<ServiceDetail> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/service-catalog/${encodeURIComponent(serviceName)}`, baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Hook to fetch the service catalog with search and filtering
 */
export function useServiceCatalog(params: ServiceCatalogParams = {}) {
  return useQuery({
    queryKey: serviceCatalogKeys.list(params),
    queryFn: () => fetchServiceCatalog(params),
    staleTime: 5 * 60 * 1000, // 5 minutes - services don't change often
    gcTime: 30 * 60 * 1000 // 30 minutes cache time
  })
}

/**
 * Hook to fetch a single service by name
 */
export function useServiceDetail(serviceName: string | null) {
  return useQuery({
    queryKey: serviceCatalogKeys.detail(serviceName ?? ''),
    queryFn: () => fetchServiceDetail(serviceName!),
    enabled: !!serviceName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000 // 30 minutes cache time
  })
}

// ============================================================================
// Mutation Types
// ============================================================================

/**
 * Response from the upsert services endpoint
 */
export interface UpsertServicesResponse {
  count: number
  message: string
}

// ============================================================================
// Mutation Functions
// ============================================================================

/**
 * Upsert (create or update) services in the catalog
 */
async function upsertServices(services: ServiceDetail[]): Promise<UpsertServicesResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/service-catalog', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ services })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Delete a service from the catalog by name
 */
async function deleteService(name: string): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/service-catalog/${encodeURIComponent(name)}`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to upsert (create/update) services in the catalog
 */
export function useUpsertServices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upsertServices,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceCatalogKeys.all
      })
    }
  })
}

/**
 * Hook to delete a service from the catalog
 */
export function useDeleteService() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceCatalogKeys.all
      })
    }
  })
}
